/* eslint-disable */
import AppHeader from "@saleor/components/AppHeader";
import CardSpacer from "@saleor/components/CardSpacer";
import { ConfirmButtonTransitionState } from "@saleor/components/ConfirmButton";
import Container from "@saleor/components/Container";
import Form from "@saleor/components/Form";
import Grid from "@saleor/components/Grid";
import PageHeader from "@saleor/components/PageHeader";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import {
  RotaryDetailsFragment,
  RotaryDetailsFragment_items
} from "@saleor/fragments/types/RotaryDetailsFragment";
import { RotaryErrorFragment } from "@saleor/fragments/types/RotaryErrorFragment";
import { sectionNames } from "@saleor/intl";
import { maybe, splitDateTime } from "@saleor/misc";
import { ReorderAction } from "@saleor/types";
import React from "react";
import { useIntl } from "react-intl";

import RotaryDetails from "../RotaryDetails";
import RotaryItems from "../RotaryItems";
import RotaryDates from "../RotaryDates";
import { LanguageCodeEnum, RotaryTypeEnum } from "@saleor/types/globalTypes";
import useNotifier from "@saleor/hooks/useNotifier";

export interface RotaryPageProps {
  rotary: RotaryDetailsFragment | null;
  disabled: boolean;
  errors: RotaryErrorFragment[];
  saveButtonBarState: ConfirmButtonTransitionState;
  items: RotaryDetailsFragment_items[];
  onBack: () => void;
  onDelete: () => void;
  onSubmit: (data: RotaryPageFormData) => void;
  onItemsAdd: () => void;
  onValueDelete: (id: string) => void;
  onValueReorder: ReorderAction;
  onValueUpdate: (id: string) => void;
  availableLngs: { id: LanguageCodeEnum; name: string }[];
  rotaryUpdateLanguage: (languageCode: string) => void;
}

export interface RotaryPageFormData {
  theme: string;
  number: number;
  isEnable?: boolean;
  isAffUse: boolean;
  hasEndDateTime: boolean;
  startDate: string;
  endDate: string;
  titleText: string;
  subtitleText: string;
  citationText: string;
  languageCode: string;
  // rulesText: string;
  titleImage: string;
  subtitleImage: string;
  backgroundImage: string;
  subjectImage: string;
  voucherImage: string;
  backgroundFromColor: string;
  backgroundToColor: string;
  buttomFromColor: string;
  buttomToColor: string;
  buttomTextColor: string;
  subjectTextColor: string;
  buttomFrameColor: string;
  rotaryTextColor: string;
  inputFrameColor: string;
  endTime: string;
  startTime: string;
  titleImageMb: string;
  subtitleImageMb: string;
  backgroundImageMb: string;
  subjectImageMb: string;
  voucherImageMb: string;
  voucherTextColor: string;
  rotaryType: RotaryTypeEnum;
  coreImage: string;
  coreImageMb: string;
  tasselsImage: string;
  tasselsImageMb: string;
  thumbnailPc: string;
  thumbnailMb: string;
  limitImage: string;
  limitImageMb: string;
  endImage: string;
  endImageMb: string;
}

const RotaryPage: React.FC<RotaryPageProps> = ({
  rotary,
  disabled,
  errors,
  saveButtonBarState,
  items,
  onBack,
  onDelete,
  onSubmit,
  onItemsAdd,
  onValueDelete,
  onValueReorder,
  onValueUpdate,
  availableLngs,
  rotaryUpdateLanguage
}) => {
  const intl = useIntl();
  const initMaybe = {
    hasEndDateTime: maybe(() => !!rotary.endDate, false),
    isEnable: maybe(() => rotary.isEnable, false),
    isAffUse: maybe(() => rotary.isAffUse, true),
    number: maybe(() => rotary.number, 1),
    theme: maybe(() => rotary.theme, ""),
    titleText: maybe(() => rotary.titleText, ""),
    subtitleText: maybe(() => rotary.subtitleText, ""),
    citationText: maybe(() => rotary.citationText, ""),
    languageCode: maybe(() => rotary.languageCode, "en"),
    // rulesText: maybe(() => rotary.rulesText, ""),
    titleImage: maybe(() => rotary.titleImage, ""),
    titleImageMb: maybe(() => rotary.titleImageMb, ""),
    subtitleImage: maybe(() => rotary.subtitleImage, ""),
    subtitleImageMb: maybe(() => rotary.subtitleImageMb, ""),
    backgroundImage: maybe(() => rotary.backgroundImage, ""),
    backgroundImageMb: maybe(() => rotary.backgroundImageMb, ""),
    subjectImage: maybe(() => rotary.subjectImage, ""),
    subjectImageMb: maybe(() => rotary.subjectImageMb, ""),
    voucherImage: maybe(() => rotary.voucherImage, ""),
    voucherImageMb: maybe(() => rotary.voucherImageMb, ""),
    voucherTextColor: maybe(() => rotary.voucherTextColor, ""),
    backgroundFromColor: maybe(() => rotary.backgroundFromColor, ""),
    backgroundToColor: maybe(() => rotary.backgroundToColor, ""),
    buttomFromColor: maybe(() => rotary.buttomFromColor, ""),
    buttomToColor: maybe(() => rotary.buttomToColor, ""),
    buttomTextColor: maybe(() => rotary.buttomTextColor, ""),
    subjectTextColor: maybe(() => rotary.subjectTextColor, ""),
    inputFrameColor: maybe(() => rotary.inputFrameColor, ""),
    buttomFrameColor: maybe(() => rotary.buttomFrameColor, ""),
    rotaryTextColor: maybe(() => rotary.rotaryTextColor, ""),
    startDate: splitDateTime(maybe(() => rotary.startDate, "")).date || "",
    startTime: splitDateTime(maybe(() => rotary.startDate, "")).time || "00:00",
    endDate: splitDateTime(maybe(() => rotary.endDate, "")).date || "",
    endTime: splitDateTime(maybe(() => rotary.endDate, "")).time || "00:00",
    rotaryType: maybe(() => rotary.rotaryType, RotaryTypeEnum.TURNTABLE),
    coreImage: maybe(() => rotary.coreImage, ""),
    coreImageMb: maybe(() => rotary.coreImageMb, ""),
    tasselsImage: maybe(() => rotary.tasselsImage, ""),
    tasselsImageMb: maybe(() => rotary.tasselsImageMb, ""),
    thumbnailPc: maybe(() => rotary.thumbnailPc, ""),
    thumbnailMb: maybe(() => rotary.thumbnailMb, ""),
    limitImage: maybe(() => rotary.limitImage, ""),
    limitImageMb: maybe(() => rotary.limitImageMb, ""),
    endImage: maybe(() => rotary.endImage, ""),
    endImageMb: maybe(() => rotary.endImageMb, "")
  };
  const notify = useNotifier();
  const initialForm: RotaryPageFormData =
    rotary === null
      ? {
          ...initMaybe
        }
      : {
          ...initMaybe
        };
  const ScrollSlide = async () => {
    const rotaryItemsTop = document.getElementById("rotaryItems").offsetTop;
    // console.log(rotaryItemsTop, "scrollTop");
    window &&
      window.scrollTo({
        top: rotaryItemsTop,
        behavior: "smooth"
      });
  };
  const handleSubmit = (data: RotaryPageFormData) => {
    if (data.rotaryType === RotaryTypeEnum.TURNTABLE && items.length !== 4) {
      ScrollSlide();
      notify({
        status: "warning",
        text: "Rotary must set four rotary items."
      });
      return undefined;
    }
    onSubmit({
      ...data
    });
  };
  return (
    <Form initial={initialForm} onSubmit={handleSubmit}>
      {({ change, data, hasChanged, setNameValue, submit }) => (
        <Container>
          <AppHeader onBack={onBack}>
            {intl.formatMessage(sectionNames.rotary)}
          </AppHeader>
          <PageHeader
            title={
              rotary === null
                ? intl.formatMessage({
                    defaultMessage: "Create New Rotary",
                    description: "page title"
                  })
                : maybe(() => rotary.theme)
            }
          />
          <Grid>
            <div>
              <RotaryDetails
                data={data}
                disabled={disabled}
                errors={errors}
                onChange={change}
                availableLngs={availableLngs}
                rotaryUpdateLanguage={rotaryUpdateLanguage}
                setNameValue={setNameValue}
              />
              <CardSpacer />
              <RotaryItems
                data={data}
                disabled={disabled}
                items={items}
                onItemsAdd={onItemsAdd}
                onValueDelete={onValueDelete}
                onValueReorder={onValueReorder}
                onValueUpdate={onValueUpdate}
              />
              <CardSpacer />
              <RotaryDates
                data={data}
                disabled={disabled}
                errors={errors}
                onChange={change}
              />
            </div>
          </Grid>
          <SaveButtonBar
            disabled={disabled || !hasChanged}
            state={saveButtonBarState}
            onCancel={onBack}
            onSave={submit}
            onDelete={rotary === null ? undefined : onDelete}
          />
        </Container>
      )}
    </Form>
  );
};
RotaryPage.displayName = "RotaryPage";
export default RotaryPage;
